import { api } from './api-client';

export const MAX_ATTACHMENT_SIZE_MB = 35;
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

export function partitionAttachmentFiles(files: File[]): { accepted: File[]; rejected: File[] } {
  return files.reduce<{ accepted: File[]; rejected: File[] }>((result, file) => {
    result[file.size > MAX_ATTACHMENT_SIZE_BYTES ? 'rejected' : 'accepted'].push(file);
    return result;
  }, { accepted: [], rejected: [] });
}

export function attachmentSizeError(files: File[]): string {
  return `Each attachment must be ${MAX_ATTACHMENT_SIZE_MB} MB or smaller. Not added: ${files.map((file) => file.name).join(', ')}`;
}

export interface PresignedUpload {
  uploadUrl: string;
  storageKey: string;
  expiresIn: number;
}

interface PresignResponse {
  data: PresignedUpload;
}

interface ConfirmResponse {
  data: { id: string; fileName: string; fileSize: number; contentType: string; storageKey: string };
}

export type AttachmentUploadStage = 'upload' | 'confirm';

export class AttachmentUploadError extends Error {
  constructor(
    message: string,
    readonly stage: AttachmentUploadStage,
    readonly session: PresignedUpload,
  ) {
    super(message);
    this.name = 'AttachmentUploadError';
  }
}

/**
 * Upload a file attachment using presigned S3 URLs (production) with automatic
 * fallback to the legacy multipart POST (local dev without S3).
 *
 * The presigned flow bypasses the ALB WAF's fixed 8KB body-inspection limit
 * by uploading directly to S3 from the browser — only tiny JSON payloads
 * (<1KB) pass through the ALB.
 */
async function uploadDirect(
  file: File,
  params: { ticketId: string; conversationId?: string },
  session: PresignedUpload,
): Promise<{ id: string }> {
  let uploadError: unknown;
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const uploadRes = await fetch(session.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!uploadRes.ok) {
        throw new Error(`S3 upload failed: ${uploadRes.status} ${uploadRes.statusText}`);
      }
      uploadError = undefined;
      break;
    } catch (error: unknown) {
      uploadError = error;
    }
  }
  if (uploadError) {
    throw new AttachmentUploadError('S3 upload failed after retrying the same upload', 'upload', session);
  }

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const confirm = await api.post<ConfirmResponse>('/attachments/confirm', {
        storageKey: session.storageKey,
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type,
        ticketId: params.ticketId,
        conversationId: params.conversationId,
      });
      return { id: confirm.data.id };
    } catch {
      if (attempt === 1) break;
    }
  }
  throw new AttachmentUploadError('Attachment confirmation failed after retrying the same upload', 'confirm', session);
}

export async function uploadAttachment(
  file: File,
  params: { ticketId: string; conversationId?: string },
  existingSession?: PresignedUpload,
): Promise<{ id: string }> {
  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) throw new Error(attachmentSizeError([file]));
  try {
    const session = existingSession ?? (await api.post<PresignResponse>('/attachments/presign', {
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type,
      ticketId: params.ticketId,
      conversationId: params.conversationId,
    })).data;
    return await uploadDirect(file, params, session);
  } catch (error: unknown) {
    // Never switch to a fresh key after an ambiguous S3 PUT or confirm. The
    // uploader retains this session and retries the same object key instead.
    if (error instanceof AttachmentUploadError) throw error;

    // ponytail: fallback to legacy multipart upload for local dev (no S3)
    // or if presign endpoint isn't deployed yet.
    const isPresignUnavailable = error instanceof Error && (
      error.message.includes('Presigned uploads require S3')
      || error.message.includes('S3 storage')
    );
    if (isPresignUnavailable) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ticketId', params.ticketId);
      if (params.conversationId) formData.append('conversationId', params.conversationId);
      const res = await api.upload<{ data: { id: string } }>('/attachments', formData);
      return { id: res.data.id };
    }
    throw error;
  }
}
